import React, { useContext, useMemo, useRef, useState, useEffect } from "react";
import { Check, Search, ChevronRight, ChevronDown } from "lucide-react";
import { getData } from "~/functions/gridFunctions";
import { useField } from "@unform/core";

function buildHierarchy(accounts) {
  const map = {};
  const roots = [];
  accounts.forEach(a => { map[a.code] = { ...a, children: [] }; });
  accounts.forEach(a => {
    const parts = a.code.split(".");
    if (parts.length > 1) {
      parts.pop();
      const parent = parts.join(".");
      if (map[parent]) map[parent].children.push(map[a.code]);
      else roots.push(map[a.code]);
    } else roots.push(map[a.code]);
  });
  return roots;
}

function getAllChildren(account, allAccounts) {
  const accountMap = {};
  allAccounts.forEach(acc => { accountMap[acc.code] = { ...acc, children: [] }; });
  allAccounts.forEach(acc => {
    const parts = acc.code.split(".");
    if (parts.length > 1) {
      parts.pop();
      const parent = parts.join(".");
      if (accountMap[parent]) accountMap[parent].children.push(accountMap[acc.code]);
    }
  });
  function collect(node) {
    let res = [node];
    if (node.children?.length) node.children.forEach(c => { res = res.concat(collect(c)); });
    return res;
  }
  return accountMap[account.code] ? collect(accountMap[account.code]) : [account];
}

function HierarchyRow({ account, onRemove, level = 0 }) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = account.children && account.children.length > 0;
  return (
    <>
      <li className="flex items-center justify-between px-2 py-1 text-xs border-t hover:bg-gray-50">
        <div style={{ marginLeft: `${level * 20}px` }} className="flex items-center gap-1 flex-1">
          {hasChildren ? (
            <button type="button" onClick={() => setExpanded(!expanded)} className="text-gray-500 hover:text-gray-700">
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : <span className="w-3.5" />}
          <span>{account.code} - {account.name}</span>
        </div>
        <button type="button" onClick={() => onRemove(account.id)} className="px-2 py-1 text-white bg-red-600 hover:bg-red-700 rounded-sm ml-2" title="Remove">
          X
        </button>
      </li>
      {expanded && hasChildren && (
        <ul>
          {account.children.map(child => (
            <HierarchyRow key={child.id} account={child} onRemove={onRemove} level={level + 1} />
          ))}
        </ul>
      )}
    </>
  );
}

export default function AccountsMultiSelect({
  title,
  name,
  options = [],
  placeholder = "Type to search...",
  InputContext,
  defaultSelected = [],
  useApi = true,
  route = "chartofaccounts",
  filterPrefix = null,
}) {
  const { setSuccessfullyUpdated } = useContext(InputContext);
  const inputRef = useRef();
  const { fieldName, registerField, defaultValue } = useField(name);

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [applied, setApplied] = useState([]);
  const [rows, setRows] = useState(options || []);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const searchRef = useRef();

  const list = useMemo(() => (useApi ? rows : options), [useApi, rows, options]);
  const prefixList = useMemo(
    () => (filterPrefix ? list.filter(o => (o.code || "").startsWith(filterPrefix)) : list),
    [list, filterPrefix]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return prefixList;
    return prefixList.filter(
      o =>
        o.code.toLowerCase().includes(q) ||
        o.name.toLowerCase().includes(q) ||
        (o.type && o.type.toLowerCase().includes(q)) ||
        `${o.code} - ${o.name}`.toLowerCase().includes(q)
    );
  }, [prefixList, query]);

  const appliedHierarchy = useMemo(() => buildHierarchy(applied), [applied]);

  async function loadAll(search = null) {
    if (!useApi) return options || [];
    setLoading(true);
    try {
      const data = await getData(route, {
        limit: 1000,
        page: 1,
        search: search ? { value: search } : null,
        setPages: () => null,
        setGridDetails: () => null,
        defaultOrderBy: { column: null, asc: true },
      });
      const loadedData = Array.isArray(data) ? data : [];
      setRows(loadedData);
      return loadedData;
    } catch (error) {
      console.error("Error loading accounts:", error);
      return [];
    } finally {
      setLoading(false);
    }
  }

  async function setByCodes(codes) {
    console.log(`[${name}] setByCodes called with:`, codes);
    
    if (!Array.isArray(codes) || codes.length === 0) {
      console.log(`[${name}] No codes to set`);
      setApplied([]);
      setSelected([]);
      return;
    }
    
    let source;
    if (useApi) {
      if (rows.length === 0) {
        console.log(`[${name}] Loading data from API`);
        const loadedData = await loadAll();
        source = loadedData;
      } else {
        console.log(`[${name}] usando existing rows data`);
        source = rows;
      }
    } else {
      source = options;
    }
    
    console.log(`[${name}] source data lenght:`, source.length);
    const matched = source.filter(r => codes.includes(r.code));
    console.log(`[${name}] Matched accounts:`, matched);
    
    setApplied(matched);
    setSelected(matched);
  }

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      getValue: () => applied.map(s => s.code),
      setValue: async (ref, value) => {
        if (!value || value.length === 0) {
          setApplied([]);
          setSelected([]);
          return;
        }
        await setByCodes(value);
      },
      clearValue: () => {
        setApplied([]);
        setSelected([]);
      },
    });
  }, [fieldName, applied]);

  useEffect(() => {
    async function initializeData() {
      if (initialized) return;
      
      const initialCodes = defaultValue || defaultSelected.map(s => s.code) || [];
      
      if (initialCodes.length > 0) {
        if (useApi && rows.length === 0) {
          await loadAll();
        }
        await setByCodes(initialCodes);
      } else if (defaultSelected.length > 0) {
        setApplied(defaultSelected);
        setSelected(defaultSelected);
      }
      
      setInitialized(true);
    }
    
    initializeData();
  }, [defaultValue]);
  
  useEffect(() => {
    if (rows.length > 0 && defaultValue && defaultValue.length > 0 && applied.length === 0 && !initialized) {
      setByCodes(defaultValue);
    }
  }, [rows]);

  function commit() {
    setApplied(selected);
  }

  function toggle(item) {
    const allChildren = getAllChildren(item, list);
    const allSelected = allChildren.every(child => selected.some(s => s.id === child.id));
    let next;
    if (allSelected) {
      next = selected.filter(s => !allChildren.some(child => child.id === s.id));
    } else {
      const toAdd = allChildren.filter(child => !selected.some(s => s.id === child.id));
      next = [...selected, ...toAdd];
    }
    setSelected(next);
  }

  function remove(id) {
    const accountToRemove = applied.find(s => s.id === id);
    if (!accountToRemove) return;

    const allToRemove = getAllChildren(accountToRemove, applied);
    const idsToRemove = allToRemove.map(acc => acc.id);

    const next = applied.filter(s => !idsToRemove.includes(s.id));
    setApplied(next);
    setSelected(prev => prev.filter(s => !idsToRemove.includes(s.id)));
  }

  return (
    <div className="flex flex-col gap-2 p-4 border-b">
      <div className="text-sm font-medium">{title}</div>
      <input type="hidden" ref={inputRef} name={fieldName} />
      <div className="w-full flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 bg-white border rounded-sm p-2 flex-1">
            <Search size={16} className="text-gray-500" />
            <input
              ref={searchRef}
              type="text"
              placeholder={placeholder}
              className="flex-1 outline-hidden text-sm"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <button
            type="button"
            onClick={async () => {
              const willOpen = !open;
              setOpen(willOpen);
              if (willOpen && rows.length === 0) await loadAll();
            }}
            className="px-3 py-2 border rounded-sm text-xs bg-gray-100 hover:bg-gray-200"
          >
            {open ? "Hide" : "Browse"}
          </button>
          <button
            type="button"
            onClick={() => {
              commit();
              setOpen(false);
            }}
            className="px-3 py-2 border border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white rounded-sm text-xs"
          >
            Add Selected
          </button>
        </div>

        {open && (
          <div className="max-h-96 overflow-auto border rounded-sm">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr>
                  <th className="text-left p-2 w-10">Sel</th>
                  <th className="text-left p-2">Code</th>
                  <th className="text-left p-2">Name</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td className="p-2 text-xs text-gray-500" colSpan={3}>Loading...</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td className="p-2 text-xs text-gray-500" colSpan={3}>No accounts found</td></tr>
                )}
                {!loading && filtered.map((opt) => {
                  const on = selected.some(s => s.id === opt.id);
                  return (
                    <tr key={opt.id} className="hover:bg-gray-50">
                      <td className="p-2">
                        <button
                          type="button"
                          onClick={() => toggle(opt)}
                          className={`w-6 h-6 flex items-center justify-center border rounded-sm ${on ? "bg-orange-500 text-white" : ""}`}
                          title={on ? "Remove" : "Add"}
                        >
                          {on ? <Check size={14} /> : "+"}
                        </button>
                      </td>
                      <td className="p-2">{opt.code}</td>
                      <td className="p-2">{opt.name}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <div className="text-xs text-gray-500">Selected accounts:</div>
          {applied.length === 0 ? (
            <span className="text-xs text-gray-400">No accounts selected</span>
          ) : (
            Object.entries(
              applied.reduce((acc, item) => {
                const key = item.type || "Others";
                acc[key] = acc[key] || [];
                acc[key].push(item);
                return acc;
              }, {})
            ).map(([group, items]) => {
              const groupHierarchy = buildHierarchy(items);
              return (
                <div key={group} className="border rounded-sm">
                  <div className="px-2 py-1 text-xs font-semibold bg-gray-100">{group}</div>
                  <ul>
                    {groupHierarchy.map((account) => (
                      <HierarchyRow key={account.id} account={account} onRemove={remove} level={0} />
                    ))}
                  </ul>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}